import * as React from 'react'

import type {
  ColumnDef,
  ColumnOrderState,
  ColumnPinningState,
  SortingState,
  Table,
  Updater,
  VisibilityState,
} from '@tanstack/react-table'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'

import type { TableFiltersProps } from '~/client/src/desktop/components/TableFilters/types'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import { CHECKBOX_COLUMN_WIDTH } from '~/client/src/shared/components/SharedVirtualTable/columns'
import type { IViewOptionsPanelProps } from '~/client/src/shared/components/ViewOptions/types'
import type { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import type { IColumnConfig } from '~/client/src/shared/hooks/useColumnConfig'
import { useColumnConfig } from '~/client/src/shared/hooks/useColumnConfig'
import type {
  ListModel,
  ListNode,
  ListQuery,
  ListSchema,
  ListViewState,
} from '~/client/src/shared/lists/core'
import {
  applySavedView,
  clearSelection,
  createListPipeline,
  initialQuery,
  listQueryReducer,
  patchQuery,
  resetQuery,
  serializeQuery,
  setCollapseAll,
  setIdsSelected,
  setSearch,
  setSort,
  toggleBand,
} from '~/client/src/shared/lists/core'
import { toggleAllFilteredSelection } from '~/client/src/shared/lists/toggleAllFilteredSelection'
import type { ListActivationEvent } from '~/client/src/shared/lists/useListInteraction'
import { useListInteraction } from '~/client/src/shared/lists/useListInteraction'
import { withDefaultOptionLabels } from '~/client/src/shared/lists/withDefaultOptionLabels'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import {
  isListFieldSortEligible,
  normalizeGroupingTransition,
} from './listFieldSorting'
import { toFilterProps } from './toFilterProps'
import { toViewOptionsProps } from './toViewOptionsProps'

const EMPTY_PINNED_RIGHT_COLUMN_IDS: readonly string[] = []

export type ColumnConfigOption = Pick<
  IColumnConfig,
  'dataKey' | 'label' | 'isFixed'
> &
  Partial<Pick<IColumnConfig, 'isHidden'>>

export interface UseTableListArgs<T> {
  records: readonly T[]
  schema: ListSchema<T>
  displayColumns: ColumnDef<ListNode<T>>[]
  columnConfig: {
    options: ColumnConfigOption[]
    storageKey: LocalStorageKey
    projectId?: string
    userId?: string
  }
  selectable?: boolean
  selectionColumnId?: string
  pinnedRightColumnIds?: readonly string[]
  extraFilters?: (records: readonly T[]) => readonly T[]
  initialQuery?: Partial<ListQuery>
  savedViewAliases?: Readonly<Record<string, string>>
  groupingAllowNone?: boolean
}

export interface ListSelectionApi<T> {
  selectable: boolean
  isSelected: (recordId: string) => boolean
  toggle: (recordId: string) => void
  toggleAllFiltered: () => void
  toggleBand: (bandId: string, selectableIds: readonly string[]) => void
  clear: () => void
  count: number
  selectedRecords: readonly T[]
  allFilteredSelected: boolean
  someFilteredSelected: boolean
}

export interface UseTableListResult<T> {
  model: ListModel<T>
  table: Table<ListNode<T>>
  columnWidths: Record<string, number>
  onColumnWidthsChange: (next: Record<string, number>) => void
  searchProps: { value: string; onChange: (v: string) => void }
  filterProps: TableFiltersProps
  viewOptionsProps: IViewOptionsPanelProps
  selection: ListSelectionApi<T>
  bands: {
    isAllCollapsed: boolean
    activeGroupKey: string | null
    toggle: (id: string) => void
    toggleAll: () => void
  }
  viewState: ListViewState
  applyViewState: (s: ListViewState) => void
  activateRecord: (recordId: string, event: ListActivationEvent) => boolean
}

function resolveUpdater<T>(updater: Updater<T>, current: T): T {
  if (typeof updater !== 'function') return updater

  return (updater as (old: T) => T)(current)
}

export function queryToSorting(query: ListQuery): SortingState {
  if (query.sortKey && query.sortOrder !== SortOrder.DEFAULT) {
    return [
      {
        id: query.sortKey,
        desc: query.sortOrder === SortOrder.DESC,
      },
    ]
  }

  return []
}

function setSortFromTanStack(updater: Updater<SortingState>, query: ListQuery) {
  const next = resolveUpdater(updater, queryToSorting(query))
  const userSort = next[0]

  if (!userSort) return setSort(null, SortOrder.DEFAULT)
  return setSort(userSort.id, userSort.desc ? SortOrder.DESC : SortOrder.ASC)
}

export function useTableList<T>({
  records,
  schema,
  displayColumns,
  columnConfig,
  selectable = false,
  selectionColumnId,
  pinnedRightColumnIds = EMPTY_PINNED_RIGHT_COLUMN_IDS,
  extraFilters,
  initialQuery: initialQueryOverrides,
  savedViewAliases,
  groupingAllowNone = true,
}: UseTableListArgs<T>): UseTableListResult<T> {
  const schemaWithDefaultOptionLabels = React.useMemo(
    () => withDefaultOptionLabels(schema),
    [schema],
  )
  const [query, dispatch] = React.useReducer(listQueryReducer, undefined, () =>
    initialQuery(schemaWithDefaultOptionLabels, initialQueryOverrides),
  )
  const initialQueryRef = React.useRef(initialQueryOverrides)
  const schemaRef = React.useRef(schemaWithDefaultOptionLabels)
  initialQueryRef.current = initialQueryOverrides
  schemaRef.current = schemaWithDefaultOptionLabels
  const queryForCurrentScope = React.useCallback(
    () => initialQuery(schemaRef.current, initialQueryRef.current),
    [],
  )
  React.useEffect(() => {
    dispatch(resetQuery(queryForCurrentScope()))
  }, [columnConfig.projectId, queryForCurrentScope])
  const pipelineRef = React.useRef<ReturnType<typeof createListPipeline<T>>>()
  if (!pipelineRef.current) {
    pipelineRef.current = createListPipeline<T>()
  }
  const {
    configuredColumns,
    defaultColumns,
    updateColumnsConfiguration,
    resetColumnsConfiguration,
  } = useColumnConfig({
    initialColumns: columnConfig.options,
    projectId: columnConfig.projectId,
    userId: columnConfig.userId,
    storageKey: columnConfig.storageKey,
  })
  const [columnWidths, setColumnWidths] = React.useState<
    Record<string, number>
  >({})
  const handleColumnWidthsChange = React.useCallback(
    (next: Record<string, number>) => {
      setColumnWidths(next)
    },
    [],
  )
  const filteredInputRecords = React.useMemo(
    () => (extraFilters ? extraFilters(records) : records),
    [extraFilters, records],
  )
  const model = pipelineRef.current(
    filteredInputRecords,
    schemaWithDefaultOptionLabels,
    query,
    records,
  )
  const interactionSource = React.useMemo(
    () => ({ nodes: model.nodes, selection: model.selection, dispatch }),
    [dispatch, model.nodes, model.selection],
  )
  const interaction = useListInteraction(interactionSource, {
    scopeKey: columnConfig.projectId,
  })
  const tableData = React.useMemo(() => [...model.nodes], [model.nodes])
  const sorting = React.useMemo(() => queryToSorting(query), [query])
  const sortableDisplayColumns = React.useMemo(
    () =>
      displayColumns.map(column => {
        const field = schemaWithDefaultOptionLabels.fields.find(
          candidate => candidate.key === column.id,
        )
        if (!field) return column

        return {
          ...column,
          enableSorting: isListFieldSortEligible(field, query.groupBy[0]),
        }
      }),
    [displayColumns, query.groupBy, schemaWithDefaultOptionLabels.fields],
  )
  const configuredColumnIds = React.useMemo(
    () => configuredColumns.map(column => column.dataKey),
    [configuredColumns],
  )
  const configuredColumnIdSet = React.useMemo(
    () => new Set(configuredColumnIds),
    [configuredColumnIds],
  )
  const displayColumnIds = React.useMemo(
    () =>
      displayColumns
        .map(column => column.id)
        .filter((id): id is string => !!id),
    [displayColumns],
  )
  const columnOrder = React.useMemo<ColumnOrderState>(() => {
    const pinnedRightColumnIdSet = new Set(pinnedRightColumnIds)
    const remainingColumnIds = displayColumnIds.filter(
      id =>
        id !== selectionColumnId &&
        !configuredColumnIdSet.has(id) &&
        !pinnedRightColumnIdSet.has(id),
    )

    return [
      ...(selectionColumnId ? [selectionColumnId] : []),
      ...configuredColumnIds,
      ...remainingColumnIds,
      ...pinnedRightColumnIds,
    ]
  }, [
    configuredColumnIdSet,
    configuredColumnIds,
    displayColumnIds,
    pinnedRightColumnIds,
    selectionColumnId,
  ])
  const columnVisibility = React.useMemo<VisibilityState>(
    () =>
      configuredColumns.reduce<VisibilityState>((acc, column) => {
        acc[column.dataKey] = !column.isHidden
        return acc
      }, {}),
    [configuredColumns],
  )
  const columnPinning = React.useMemo<ColumnPinningState>(
    () => ({
      left: [
        ...(selectionColumnId ? [selectionColumnId] : []),
        ...configuredColumns
          .filter(column => column.isFixed && !column.isHidden)
          .map(column => column.dataKey),
      ],
      right: [...pinnedRightColumnIds],
    }),
    [configuredColumns, pinnedRightColumnIds, selectionColumnId],
  )
  const filteredSelectableIds = selectable
    ? model.selection.filteredSelectableIds
    : []
  const allFilteredSelected = selectable && model.selection.allFilteredSelected
  const someFilteredSelected =
    selectable && model.selection.someFilteredSelected
  const selection = React.useMemo<ListSelectionApi<T>>(
    () => ({
      selectable,
      isSelected: recordId =>
        selectable && model.selection.selectedIds.has(recordId),
      toggle: recordId => {
        if (selectable) interaction.toggleFor(recordId)
      },
      toggleAllFiltered: () => {
        if (!selectable) return
        toggleAllFilteredSelection(model.selection, dispatch)
      },
      toggleBand: (bandId, selectableIds) => {
        if (!selectable) return
        const allBandSelected = model.bandSelection.get(bandId) === 'all'
        dispatch(setIdsSelected(selectableIds, !allBandSelected))
      },
      clear: () => {
        dispatch(clearSelection())
      },
      count: selectable ? model.selection.count : 0,
      selectedRecords: selectable ? model.selection.selectedRecords : [],
      allFilteredSelected,
      someFilteredSelected,
    }),
    [
      allFilteredSelected,
      filteredSelectableIds,
      model.bandSelection,
      model.selection.count,
      model.selection.selectedIds,
      model.selection.selectedRecords,
      selectable,
      someFilteredSelected,
      interaction,
    ],
  )
  const selectionColumn = React.useMemo<ColumnDef<ListNode<T>> | null>(() => {
    if (!selectable || !selectionColumnId) return null

    return {
      id: selectionColumnId,
      header: () =>
        React.createElement(
          'span',
          {
            className:
              'tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center',
            onClick: (event: React.MouseEvent) => {
              event.stopPropagation()
              selection.toggleAllFiltered()
            },
          },
          React.createElement(Checkbox, {
            checked: selection.allFilteredSelected,
            indeterminate: selection.someFilteredSelected,
            onCheckedChange: selection.toggleAllFiltered,
            'aria-label': Localization.translator.select,
          }),
        ),
      size: CHECKBOX_COLUMN_WIDTH,
      maxSize: CHECKBOX_COLUMN_WIDTH,
      enableSorting: false,
      meta: { align: 'center', cellClassName: 'tw:px-0' },
      cell: ({ row }) => {
        const node = row.original
        if (node.kind !== 'record') return null
        return React.createElement(
          'span',
          {
            className:
              'tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center',
            onMouseDown: interaction.onSelectionMouseDown,
            onClick: (event: React.MouseEvent) => {
              event.stopPropagation()
              interaction.toggleFor(node.recordId, event)
            },
          },
          React.createElement(Checkbox, {
            checked: selection.isSelected(node.recordId),
            onCheckedChange: (_checked, details) => {
              const event = details.event
              interaction.toggleFor(node.recordId, {
                shiftKey:
                  event instanceof MouseEvent || event instanceof KeyboardEvent
                    ? event.shiftKey
                    : false,
              })
            },
            'aria-label': Localization.translator.select,
          }),
        )
      },
    }
  }, [interaction, selectable, selection, selectionColumnId])
  const columns = React.useMemo(
    () =>
      selectionColumn
        ? [selectionColumn, ...sortableDisplayColumns]
        : sortableDisplayColumns,
    [selectionColumn, sortableDisplayColumns],
  )
  const table = useReactTable<ListNode<T>>({
    data: tableData,
    columns,
    state: {
      columnOrder,
      columnPinning,
      columnVisibility,
      sorting,
    },
    autoResetExpanded: false,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getRowId: node => node.id,
    manualSorting: true,
    onSortingChange: updater => {
      dispatch(setSortFromTanStack(updater, query))
    },
  })
  const searchProps = React.useMemo(
    () => ({
      value: query.search,
      onChange: (value: string) => {
        dispatch(setSearch(value))
      },
    }),
    [query.search],
  )
  const filterProps = React.useMemo(
    () =>
      toFilterProps(
        model,
        query,
        dispatch,
        filteredInputRecords,
        schemaWithDefaultOptionLabels,
      ),
    [filteredInputRecords, model, query, schemaWithDefaultOptionLabels],
  )
  const handleResetColumns = React.useCallback(() => {
    resetColumnsConfiguration()
    setColumnWidths({})
  }, [resetColumnsConfiguration])
  const viewOptionsProps = React.useMemo(
    () =>
      toViewOptionsProps(
        query,
        dispatch,
        schemaWithDefaultOptionLabels,
        {
          items: configuredColumns,
          defaultItems: defaultColumns,
          onChange: updateColumnsConfiguration,
          onReset: handleResetColumns,
        },
        groupingAllowNone,
        queryForCurrentScope(),
      ),
    [
      configuredColumns,
      defaultColumns,
      groupingAllowNone,
      handleResetColumns,
      query,
      queryForCurrentScope,
      schemaWithDefaultOptionLabels,
      updateColumnsConfiguration,
    ],
  )
  const bands = React.useMemo(
    () => ({
      isAllCollapsed: model.isAllCollapsed,
      activeGroupKey: query.groupBy[0] ?? null,
      toggle: (id: string) => {
        dispatch(toggleBand(id))
      },
      toggleAll: () => {
        dispatch(setCollapseAll(model.bandIds, !model.isAllCollapsed))
      },
    }),
    [model.bandIds, model.isAllCollapsed, query.groupBy],
  )
  const viewState = React.useMemo(() => serializeQuery(query), [query])
  const applyViewState = React.useCallback(
    (state: ListViewState) => {
      const viewPatch = applySavedView(
        state,
        schemaWithDefaultOptionLabels,
        savedViewAliases,
      )
      dispatch(
        patchQuery({
          ...viewPatch,
          ...normalizeGroupingTransition(
            query,
            viewPatch.groupBy ?? query.groupBy,
            schemaWithDefaultOptionLabels,
          ),
        }),
      )
    },
    [query, savedViewAliases, schemaWithDefaultOptionLabels],
  )

  return {
    model,
    table,
    columnWidths,
    onColumnWidthsChange: handleColumnWidthsChange,
    searchProps,
    filterProps,
    viewOptionsProps,
    selection,
    bands,
    viewState,
    applyViewState,
    activateRecord: selectable ? interaction.activateFor : () => true,
  }
}
